import { z } from "zod/v4";
import { reportStatusSchema } from "./report";

export const locationVerificationSchema = z.enum([
	"unverified",
	"player_verified",
	"admin_verified",
]);
export type LocationVerification = z.infer<typeof locationVerificationSchema>;

export const locationReportReasonSchema = z.enum([
	"wrong_place",
	"duplicate",
	"spam",
	"offensive",
	"other",
]);
export type LocationReportReason = z.infer<typeof locationReportReasonSchema>;

export const locationReportSchema = z.object({
	created_at: z.coerce.date(),
	details: z.string().nullable().optional(),
	id: z.string().uuid(),
	location_city: z.string().nullable().optional(),
	location_id: z.string().uuid(),
	location_name: z.string().nullable().optional(),
	reason: locationReportReasonSchema,
	reporter_avatar_url: z.string().nullable().optional(),
	reporter_email: z.string().nullable().optional(),
	reporter_id: z.string().uuid(),
	reporter_name: z.string().nullable().optional(),
	resolved_at: z.coerce.date().nullable().optional(),
	resolved_by: z.string().uuid().nullable().optional(),
	status: reportStatusSchema,
});
export type LocationReport = z.infer<typeof locationReportSchema>;

export const locationReportSummarySchema = z.object({
	last_reported_at: z.coerce.date(),
	location_city: z.string().nullable().optional(),
	location_id: z.string().uuid(),
	location_name: z.string(),
	open_count: z.number().int().nonnegative(),
	reasons: z.array(locationReportReasonSchema),
	total_count: z.number().int().nonnegative(),
});
export type LocationReportSummary = z.infer<typeof locationReportSummarySchema>;

export const duplicateReviewStatusSchema = z.enum(["pending", "merged", "dismissed"]);
export type DuplicateReviewStatus = z.infer<typeof duplicateReviewStatusSchema>;

export const llmVerdictSchema = z.enum(["same", "different", "uncertain"]);
export type LlmVerdict = z.infer<typeof llmVerdictSchema>;

export const locationSnapshotSchema = z.object({
	address: z.string().nullable().optional(),
	city: z.string().nullable().optional(),
	id: z.string().uuid(),
	latitude: z.number(),
	longitude: z.number(),
	name: z.string(),
});
export type LocationSnapshot = z.infer<typeof locationSnapshotSchema>;

export const duplicateReviewSchema = z.object({
	candidate_location: locationSnapshotSchema,
	confidence: z.number(),
	created_at: z.coerce.date(),
	created_by: z.string().uuid().nullable(),
	creator_email: z.string().nullable().optional(),
	creator_name: z.string().nullable().optional(),
	id: z.string().uuid(),
	llm_verdict: llmVerdictSchema.nullable().optional(),
	new_location: locationSnapshotSchema,
	resolved_at: z.coerce.date().nullable().optional(),
	resolved_by: z.string().uuid().nullable().optional(),
	status: duplicateReviewStatusSchema,
});
export type DuplicateReview = z.infer<typeof duplicateReviewSchema>;

export const updateDuplicateReviewSchema = z.object({
	into: z.string().uuid().optional(),
	status: z.enum(["merged", "dismissed"]),
});
export type UpdateDuplicateReview = z.infer<typeof updateDuplicateReviewSchema>;

export const imageReviewStatusSchema = z.enum(["pending", "approved", "rejected"]);
export type ImageReviewStatus = z.infer<typeof imageReviewStatusSchema>;

export const imageReviewLocationSchema = z.object({
	address: z.string().nullable().optional(),
	city: z.string().nullable().optional(),
	id: z.string().uuid(),
	name: z.string(),
});

export const imageReviewSchema = z.object({
	caption: z.string().nullable().optional(),
	created_at: z.coerce.date(),
	created_by: z.string().uuid(),
	id: z.string().uuid(),
	location: imageReviewLocationSchema,
	resolved_at: z.coerce.date().nullable().optional(),
	resolved_by: z.string().uuid().nullable().optional(),
	status: imageReviewStatusSchema,
	uploader_email: z.string().nullable().optional(),
	uploader_name: z.string().nullable().optional(),
	url: z.string(),
});
export type ImageReview = z.infer<typeof imageReviewSchema>;

export const updateImageReviewSchema = z.object({
	status: z.enum(["approved", "rejected"]),
});
export type UpdateImageReview = z.infer<typeof updateImageReviewSchema>;

export const satelliteScreenVerdictSchema = z.enum(["court", "not_a_court", "unclear"]);
export type SatelliteScreenVerdict = z.infer<typeof satelliteScreenVerdictSchema>;

export const satelliteScreenResolutionSchema = z.enum(["pending", "accepted", "hidden"]);
export type SatelliteScreenResolution = z.infer<typeof satelliteScreenResolutionSchema>;

export const satelliteScreenLocationSchema = z.object({
	address: z.string().nullable().optional(),
	city: z.string().nullable().optional(),
	id: z.string().uuid(),
	latitude: z.number(),
	longitude: z.number(),
	name: z.string(),
});

export const satelliteScreenSchema = z.object({
	confidence: z.number(),
	created_at: z.coerce.date(),
	id: z.string().uuid(),
	image_url: z.string(),
	location: satelliteScreenLocationSchema,
	reason: z.string(),
	resolution: satelliteScreenResolutionSchema,
	resolved_at: z.coerce.date().nullable().optional(),
	verdict: satelliteScreenVerdictSchema,
});
export type SatelliteScreen = z.infer<typeof satelliteScreenSchema>;

export const updateSatelliteScreenSchema = z.object({
	resolution: z.enum(["accepted", "hidden"]),
});
export type UpdateSatelliteScreen = z.infer<typeof updateSatelliteScreenSchema>;

export const screenRunResponseSchema = z.object({
	limit: z.number().int(),
	queued: z.number().int(),
	screened: z.number().int(),
	skipped: z.number().int(),
	status: z.string(),
});
export type ScreenRunResponse = z.infer<typeof screenRunResponseSchema>;

export const mergeLocationSchema = z.object({
	into: z.string().uuid(),
});
export type MergeLocation = z.infer<typeof mergeLocationSchema>;

export const locationImageSchema = z.object({
	caption: z.string().nullable().optional(),
	id: z.string().uuid(),
	is_primary: z.boolean(),
	sort_order: z.number().int(),
	url: z.string(),
});
export type LocationImage = z.infer<typeof locationImageSchema>;

export const updateLocationImageSchema = z.object({
	caption: z.string().nullable().optional(),
	is_primary: z.boolean().optional(),
	sort_order: z.number().int().nonnegative().optional(),
});
export type UpdateLocationImage = z.infer<typeof updateLocationImageSchema>;

export const locationSourceSchema = z.enum(["official", "community", "imported"]);

export type LocationSource = z.infer<typeof locationSourceSchema>;

export const locationSchema = z.object({
	address: z.string().nullable().optional(),
	attribution: z.string().nullable().optional(),
	city: z.string().nullable().optional(),
	zipcode: z.string().nullable().optional(),
	country: z.string().optional(),
	court_count: z.number().int(),
	created_at: z.coerce.date().optional(),
	distance_km: z.number().nullable().optional(),
	id: z.string().uuid(),
	images: z
		.array(locationImageSchema)
		.optional()
		.transform((v) => v ?? []),
	is_favorite: z.boolean().optional(),
	is_hidden: z.boolean().optional(),
	latitude: z.number(),
	longitude: z.number(),
	name: z.string(),
	primary_image_url: z.string().nullable().optional(),
	review_priority: z.number().int().optional(),
	source: locationSourceSchema.optional(),
	timezone: z.string().optional(),
	verification: locationVerificationSchema.optional(),
});

export const createLocationSchema = z.object({
	address: z.string().nullable().optional(),
	city: z.string().nullable().optional(),
	zipcode: z.string().nullable().optional(),
	court_count: z.number().int().min(1),
	latitude: z.number(),
	longitude: z.number(),
	name: z.string().min(1),
});

export const updateLocationSchema = createLocationSchema.partial();

export const scrapeRunStatusSchema = z.enum(["running", "succeeded", "failed", "no_match"]);

export const imageSourceSchema = z.enum([
	"unknown",
	"google_places",
	"venue_site",
	"wikimedia",
	"unsplash",
	"mapillary",
	"manual",
]);

export const scrapeRunResultSchema = z.object({
	candidates: z.number().int(),
	error: z.string().optional(),
	location_id: z.string(),
	source: imageSourceSchema.optional(),
	status: scrapeRunStatusSchema,
	uploaded: z.number().int(),
});

export const scrapeBulkResponseSchema = z.object({
	limit: z.number().int(),
	status: z.string(),
});
